import { useTranslation } from 'react-i18next'
import { Route, Routes } from 'react-router-dom'

import PageHeader from '@/components/layouts/PageHeader'
import Shell from '@/components/layouts/Shell'
import ActivationRoutes from '@/features/activation/ActivationRoutes'
import ActivationGroupRoutes from '@/features/activationGroup/ActivationGroupRoutes'
import ActivationPlanRoutes from '@/features/activationPlan/ActivationPlanRoutes'
import { ProtectedRoute } from '@/features/authentication/components/ProtectedRoute'
import BessDashboardRoutes from '@/features/bessDashboard/BessDashboardRoutes'
import BiddingRoutes from '@/features/bidding/BiddingRoutes'
import ControllersRoutes from '@/features/controller/ControllersRoutes'
import DisturbancesRoutes from '@/features/disturbance/DisturbancesRoutes'
import ErrorRoutes from '@/features/error/ErrorsRoutes'
import NavigateToError from '@/features/error/pages/NavigateToError'
import ExampleFeatureRoutes from '@/features/exampleFeature/ExampleFeatureRoutes'
import { useFeatureToggle } from '@/features/featureToggle/contexts/FeatureToggleContext'
import FlexBalanceRoutes from '@/features/flexBalance/FlexBalanceRoutes'
import PartnerRoutes from '@/features/partner/PartnerRoutes'
import PortfolioHierarchyRoutes from '@/features/portfolioHierarchy/PortfolioHierarchyRoutes'
import PrequalificationRoutes from '@/features/prequalification/PrequalificationRoutes'
import ReportRoutes from '@/features/report/ReportRoutes'
import ResourcesRoutes from '@/features/resource/ResourcesRoutes'
import ResourceSwitchboardRoutes from '@/features/resourceSwitchboard/ResourceSwitchboardRoutes'
import RedirectToSites from '@/features/site/RedirectToSites'
import SitesRoutes from '@/features/site/SitesRoutes'
import { PrivacyPolicy } from '@/features/user/pages/PrivacyPolicy'
import { UserProfile } from '@/features/user/pages/UserProfile'
import UsersRoutes from '@/features/user/UsersRoutes'
import WholesaleRoutes from '@/features/wholesale/WholesaleRoutes'
import { LandingPage } from '@/LandingPage'

export default function AuthenticatedAppRoutes() {
  const { t } = useTranslation()
  const { isEnabled } = useFeatureToggle()

  return (
    <Routes>
      <Route element={<Shell />} path="/">
        <Route index element={<LandingPage />} />
        <Route
          element={
            <>
              <PageHeader pageTag={'privacy-policy'} pageTitle={t('component.page_header.privacy_policy')} />
              <PrivacyPolicy />
            </>
          }
          path="/privacy-policy"
        />
        <Route
          element={
            <>
              <PageHeader pageTag={'user-profile'} pageTitle={t('component.page_header.user.user_profile')} />
              <UserProfile />
            </>
          }
          path="/profile"
        />
        <Route element={<ProtectedRoute permission="bidding" />}>
          <Route element={<BiddingRoutes />} path="bidding/*" />
        </Route>
        {isEnabled('FLEX_PORTAL_ACTIVATION_PLAN_PAGE_ENABLED') && (
          <Route element={<ProtectedRoute permission="bidding" />}>
            <Route element={<ActivationPlanRoutes />} path="activation-plan/*" />
          </Route>
        )}
        <Route element={<ProtectedRoute permission="wholesale" />}>
          <Route element={<WholesaleRoutes />} path="wholesale/*" />
        </Route>
        <Route element={<ProtectedRoute permission="reports" />}>
          <Route element={<ReportRoutes />} path="reports/*" />
        </Route>
        {isEnabled('PORTFOLIO_HIERARCHY_PAGE_ENABLED') && (
          <Route element={<ProtectedRoute permission="portfolioHierarchy" />}>
            <Route element={<PortfolioHierarchyRoutes />} path="portfolio-hierarchy/*" />
          </Route>
        )}
        <Route element={<ProtectedRoute permission="resources" />}>
          <Route element={<ResourcesRoutes />} path="resources/*" />
        </Route>
        <Route element={<ProtectedRoute permission="controllers" />}>
          <Route element={<ControllersRoutes />} path="controllers/*" />
        </Route>
        <Route element={<SitesRoutes />} path="sites/*" />
        <Route element={<RedirectToSites />} path="customers/*" />
        <Route element={<ProtectedRoute permission="partners" />}>
          <Route element={<PartnerRoutes />} path="partners/*" />
        </Route>
        <Route element={<ProtectedRoute permission="site" />}>
          <Route element={<ActivationRoutes />} path="activations/*" />
        </Route>
        <Route element={<ProtectedRoute permission="users" />}>
          <Route element={<UsersRoutes />} path="users/*" />
        </Route>
        <Route element={<ProtectedRoute permission="activationGroups" />}>
          <Route element={<ActivationGroupRoutes />} path="activation-groups/*" />
        </Route>
        {isEnabled('FLEX_PORTAL_DISTURBANCES_PAGE_ENABLED') && (
          <Route element={<ProtectedRoute permission="disturbances" />}>
            <Route element={<DisturbancesRoutes />} path="disturbances/*" />
          </Route>
        )}

        {isEnabled('FLEXPORTAL_EXAMPLE_FEATURE_ENABLED') && (
          <Route element={<ProtectedRoute permission="exampleFeatures" />}>
            <Route element={<ExampleFeatureRoutes />} path="example-feature/*" />
          </Route>
        )}
        <Route element={<ProtectedRoute permission="bessDashboard" />}>
          <Route element={<BessDashboardRoutes />} path="bess-dashboard/*" />
        </Route>

        <Route element={<ProtectedRoute permission="resourceSwitchboard" />}>
          <Route element={<ResourceSwitchboardRoutes />} path="resource-switchboard/*" />
        </Route>

        <Route element={<ProtectedRoute permission="flexBalance" />}>
          <Route element={<FlexBalanceRoutes />} path="flexbalance/*" />
        </Route>

        <Route element={<ProtectedRoute permission="prequalifications" />}>
          <Route element={<PrequalificationRoutes />} path="prequalifications/*" />
        </Route>
      </Route>

      <Route element={<ErrorRoutes />} path="/errors/*" />

      <Route element={<NavigateToError statusCode={404} />} path="*" />
    </Routes>
  )
}
